import { App, Flex, Skeleton } from "antd"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { type ConnectedProps, connect } from "react-redux"
import { useNavigate, useParams } from "react-router"

import { RoutesPaths } from "../../../../../../constants/routes.ts"
import type { RootState } from "../../../../../../store.ts"
import { serializeData } from "../../../../../../utils/utils"
import { albumSetProp } from "../../../../../Album/redux/album.slice.ts"
import {
  createAlbum,
  deleteAlbum,
  fetchAlbums,
  fetchMoreAlbums,
  updateAlbum
} from "../../../../../Album/redux/album.thunks.ts"
import { type AlbumsFilters } from "../../../../../Album/redux/album.types.ts"
import type {
  Album,
  AlbumCreateData
} from "../../../../../Album/types/album.types.ts"
import { selectProjectWritePermissions } from "../../../../helpers/project-permissions.selectors.ts"
import { ALBUM_CARD_WITH } from "./CreateAlbum/CreateAlbumCard.tsx"
import { EditAlbumModal } from "./EditAlbum/EditAlbumModal.tsx"
import { ProjectAlbums } from "./ProjectAlbums/ProjectAlbums.tsx"
import { ProjectAlbumsFilters } from "./ProjectAlbums/ProjectAlbumsFilters.tsx"
import { ProjectViewAlbumsRecentFilesSection } from "./ProjectViewAlbumsRecentFilesSection.tsx"

function ProjectViewAlbumsSection({
  data,
  fetching,
  projectPermissions,
  getAlbums,
  getMoreAlbums,
  createAlbum,
  filters,
  setProp,
  userId,
  deleteAlbum,
  updateAlbum
}: ConnectedProps<typeof connector>) {
  const isInitRef = useRef(true)
  const { modal } = App.useApp()

  const isRefetching = !isInitRef?.current

  const navigate = useNavigate()
  const projectId = useParams()?.id as string

  const albums = data?.results || []
  const count = data?.count || 0
  const moduleDocs = projectPermissions?.docs

  const deleteAllowed = !!moduleDocs?.project_doc_delete
  const deleteForeignAllowed =
    deleteAllowed && !!moduleDocs?.project_doc_manage_others
  const editAllowed = !!moduleDocs?.project_doc_edit
  const [editingAlbumId, setEditingAlbumId] = useState<string>()

  const editingAlbum = useMemo(
    () => albums.find((a) => a.id === editingAlbumId),
    [albums, editingAlbumId]
  )

  const handleEditSubmit = useCallback(
    async (data: Partial<Album>) => {
      if (!editingAlbumId) return
      await updateAlbum({ id: editingAlbumId, data }).unwrap()
      setEditingAlbumId(undefined)
    },
    [updateAlbum, editingAlbumId]
  )

  const handleDelete = useCallback(
    async (albumId: string) => {
      await deleteAlbum(albumId)
    },
    [deleteAlbum]
  )

  const onDeleteClick = useCallback(
    (albumId: string) => {
      modal.confirm({
        centered: true,
        title: "Delete album",
        content:
          "Are you sure you want to delete this album? This action can NOT be undone.",
        cancelText: "Cancel",
        okText: "Delete",
        okButtonProps: { danger: true, ghost: true },
        onOk: () => handleDelete(albumId)
      })
    },
    [handleDelete]
  )

  const updateFilters = useCallback(
    (data: Partial<AlbumsFilters>) => {
      setProp({
        key: "filters",
        value: { ...filters, ...data }
      })
    },
    [setProp, filters]
  )

  const handleChangeFilter = useCallback(
    (newData: Partial<AlbumsFilters>) => {
      updateFilters({ ...newData, page: 1 })
    },
    [updateFilters]
  )

  const handleGetAlbums = useCallback(async () => {
    const serializeFilters = serializeData(filters || {})

    await getAlbums({ project: projectId, ...serializeFilters })
    if (isInitRef.current) isInitRef.current = false
  }, [projectId, filters, getAlbums])

  const handleCreateAlbum = useCallback(
    async (data: AlbumCreateData) => {
      await createAlbum({ ...data, project: projectId, order: count + 1 })
    },
    [createAlbum, projectId, count]
  )

  const handleViewAlbum = useCallback(
    (id: string) => {
      navigate(RoutesPaths.project.view(projectId).album(id))
    },
    [projectId, navigate]
  )

  const handleLoadMoreData = useCallback(() => {
    if (!data?.next) return
    const urlObj = new URL(data?.next)
    const params = new URLSearchParams(urlObj.search)
    const paramsObj = Object.fromEntries(params)
    const serializeFilters = serializeData(paramsObj) as AlbumsFilters

    getMoreAlbums({ project: projectId, ...serializeFilters })
  }, [getMoreAlbums, projectId, data?.next])

  useEffect(() => {
    void handleGetAlbums()
  }, [filters, handleGetAlbums])

  if (!isRefetching && fetching) {
    return (
      <Flex
        vertical
        gap={"small"}
        flex={1}
        className={"overflow-hidden w-full"}
      >
        <Skeleton.Node className={"w-full h-[86px]"} />
        <div
          style={{
            gridTemplateColumns: `repeat(auto-fill, minmax(${ALBUM_CARD_WITH}px, 1fr))`
          }}
          className="grid gap-4 overflow-y-auto tailwind-scroll overflow-x-hidden flex-1"
        >
          {Array.from({ length: 20 }).map((_, i) => (
            <Skeleton.Node
              key={i}
              active
              style={{ width: "100%", height: "320px" }}
              className="w-full"
            />
          ))}
        </div>
        <Skeleton.Node className={"w-full h-44"} />
      </Flex>
    )
  }

  return (
    <Flex vertical gap={"small"} flex={1} className={"overflow-hidden w-full"}>
      <ProjectAlbumsFilters
        showVisibilityFilter={
          !!projectPermissions?.docs?.project_doc_show_visibility_filter
        }
        filters={filters}
        sortOptions={[
          { key: "name", label: "Name" },
          { key: "created", label: "Created Date" }
        ]}
        onFiltersChange={handleChangeFilter}
      />

      {fetching && isRefetching ? (
        <div
          style={{
            gridTemplateColumns: `repeat(auto-fill, minmax(${ALBUM_CARD_WITH}px, 1fr))`
          }}
          className="grid gap-4 overflow-y-auto tailwind-scroll overflow-x-hidden flex-1"
        >
          {Array.from({ length: 20 }).map((_, i) => (
            <Skeleton.Node
              key={i}
              active
              style={{ width: "100%", height: "320px" }}
              className="w-full"
            />
          ))}
        </div>
      ) : (
        <ProjectAlbums
          albums={albums}
          createAllowed={moduleDocs?.project_album_create || false}
          hasSearch={!!filters?.q}
          hasMore={!!data?.next}
          onLoadMore={handleLoadMoreData}
          onCreateAlbum={handleCreateAlbum}
          onViewAlbum={handleViewAlbum}
          userId={userId}
          deleteAllowed={deleteAllowed}
          deleteForeignAllowed={deleteForeignAllowed}
          onDeleteClick={onDeleteClick}
          editAllowed={editAllowed}
          onEditClick={setEditingAlbumId}
        />
      )}
      <ProjectViewAlbumsRecentFilesSection
        uploadAllowed={moduleDocs?.project_doc_create || false}
      />

      {editingAlbum && (
        <EditAlbumModal
          album={editingAlbum}
          onClose={() => setEditingAlbumId(undefined)}
          onSubmit={handleEditSubmit}
        />
      )}
    </Flex>
  )
}

const mapStateToProps = (state: RootState) => ({
  fetching: state.album.fetching,
  data: state.album.albums,
  filters: state.album.filters,
  projectPermissions: selectProjectWritePermissions(state),
  userId: state.auth.userInfo.id
})

const mapDispatchToProps = {
  getAlbums: fetchAlbums,
  getMoreAlbums: fetchMoreAlbums,
  createAlbum,
  setProp: albumSetProp,
  deleteAlbum,
  updateAlbum
}

const connector = connect(mapStateToProps, mapDispatchToProps)
export default connector(ProjectViewAlbumsSection)
